import React, { Component } from 'react';
import PropTypes from 'prop-types';
import root from 'window-or-global';
import storage from 'web-storage';
import get from 'lodash/get';

import config from 'config';
import { formatCurrency } from 'utils';
import Redirect from 'routing/redirect';
import Container from 'common/container';
import { ContributePageProvider } from './context';
import SEO from './seo';
import Header from './header';
import SelectType from './select-type';
import SelectAmount from './select-amount';
import BackpledgePrompt from './backpledge-prompt';
import EnterPayment from './enter-payment';
import Confirmation from './confirm';
import DonationTimeout from './donation-timeout';

const { localStorage } = storage();

class ContributePage extends Component {
    static propTypes = {
        campaign: PropTypes.shape({
            id: PropTypes.string,
            slug: PropTypes.string,
            name: PropTypes.string,
            eventsEndAt: PropTypes.string,
            isComplete: PropTypes.bool,
            isDonationOnly: PropTypes.bool,
            stats: PropTypes.shape({
                eventFinalizedCount: PropTypes.number,
                eventRemainingCount: PropTypes.number,
                performanceActualTotal: PropTypes.number,
            }),
            performances: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                })
            ),
            thirdPartyTerms: PropTypes.shape({
                statement: PropTypes.string,
                extended: PropTypes.string,
            }),
            shouldDisplayBenefitInHero: PropTypes.bool,
            benefits: PropTypes.string,
        }).isRequired,
        step: PropTypes.oneOf(['type', 'amount', 'payment', 'confirm', 'thanks']).isRequired,
        createContribution: PropTypes.func.isRequired,
        showFlash: PropTypes.func.isRequired,
        navigate: PropTypes.func.isRequired,
        type: PropTypes.oneOf(['pledge', 'donate']),
        backpledge: PropTypes.bool,
        amount: PropTypes.number,
        tip: PropTypes.number,
        token: PropTypes.string,
        card: PropTypes.shape({
            brand: PropTypes.string,
            last4: PropTypes.string,
        }),
        user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            email: PropTypes.string,
        }),
        fundraiser: PropTypes.string,
        displayName: PropTypes.string,
        isMatched: PropTypes.bool,
        isReferrerLocked: PropTypes.bool,
    };

    static defaultProps = {
        type: null,
        backpledge: false,
        amount: 0,
        tip: 0,
        token: null,
        card: null,
        user: null,
        fundraiser: null,
        isMatched: undefined,
        displayName: undefined,
        isReferrerLocked: false,
    };

    state = {
        /* eslint-disable react/destructuring-assignment */
        type: this.props.type,
        backpledge: this.props.backpledge,
        amount: this.props.amount,
        tip: this.props.tip,
        token: this.props.token,
        card: this.props.card,
        user: this.props.user,
        fundraiser: this.props.fundraiser,
        isMatched: this.props.isMatched,
        displayName: this.props.displayName,
        shouldPromptBackpledge: false,
        shouldShowDonationTimeout: false,
        thirdPartyTermsAccepted: false,
        /* eslint-disable react/destructuring-assignment */
    };

    get canPledge() {
        const { campaign } = this.props;
        // Get finalized events remaining from performances.length instead of
        // eventRemainingCount for old campaigns that don't have proper stats
        return (
            campaign.performances &&
            campaign.performances.filter(x => x.isFinal).length < campaign.performances.length
        );
    }

    get canBackpledge() {
        const { campaign } = this.props;
        return (
            // Get finalized event count from performances.length instead of
            // eventFinalCount for old campaigns that don't have proper stats
            this.canPledge &&
            !!campaign.performances.filter(x => x.isFinal).length &&
            !!get(campaign, 'stats.performanceActualTotal', 0)
        );
    }

    get urls() {
        const { campaign } = this.props;
        const base = `/${campaign.slug}`;

        return {
            base,
            type: `${base}/contribute`,
            donateAmount: `${base}/donate`,
            pledgeAmount: `${base}/pledge`,
            payment: `${base}/contribute/payment`,
            confirm: `${base}/contribute/confirm`,
        };
    }

    get prevStepUrl() {
        const { step } = this.props;
        const { type } = this.state;

        switch (step) {
            case 'type':
                return this.urls.base;
            case 'payment':
                return type ? this.urls[`${type}Amount`] : this.urls.type;
            case 'confirm':
                return this.urls.payment;
            default:
                return this.urls.type;
        }
    }

    handleZendesk = () => {
        root.zE(() => root.zE.activate({ hideOnClose: true }));
    };

    handleTypeSubmit = type => {
        const { navigate } = this.props;

        this.setState({ type });
        navigate(this.urls[`${type}Amount`]);
    };

    handleAmountSubmit = (amount, estimated) => {
        const { showFlash } = this.props;
        const { type } = this.state;

        this.setState({ amount });

        /* Requires minimum estimated total of 50 cents */
        const minimumCharge = config('/minimum_donation_amount');
        if (estimated < minimumCharge) {
            showFlash({
                type: 'danger',
                title: 'Minimum pledge required',
                message: `You must make a pledge estimated to be greater than ${formatCurrency(
                    minimumCharge
                )} when totaled. Please try again.`,
                duration: 5000,
            });
            return;
        }

        if (type === 'pledge' && this.canBackpledge) {
            this.setState({ shouldPromptBackpledge: true });
        } else {
            this.handleBackpledgeSubmit(false);
        }
    };

    handleBackpledgeSubmit = backpledge => {
        const { navigate } = this.props;
        this.setState({ backpledge, shouldPromptBackpledge: false });
        navigate(this.urls.payment);
    };

    handlePaymentSubmit = data => {
        const { navigate } = this.props;
        this.setState(data);
        navigate(this.urls.confirm);
    };

    handleConfirm = async ({ setSubmitting }) => {
        const { campaign, showFlash, createContribution, navigate } = this.props;
        const {
            type,
            amount,
            tip,
            token,
            user,
            backpledge,
            fundraiser,
            isMatched,
            thirdPartyTermsAccepted,
            displayName,
        } = this.state;

        const contributionName = type === 'pledge' ? 'pledge' : 'donation';

        try {
            setSubmitting(true);

            const data = {
                campaign: campaign.id,
                type: contributionName,
                tip: tip || 0,
                amount,
                token,
                fundraiser: fundraiser || undefined,
                referralTrackingId: localStorage.get(`referral.${campaign.id}.${fundraiser}`),
                performances:
                    type === 'pledge'
                        ? campaign.performances.filter(x => !x.isFinal || backpledge).map(x => x.id)
                        : undefined,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                thirdPartyTermsAccepted,
                displayName,
            };

            if (user.address) Object.assign(data, user.address);
            if (isMatched === false) data.matchDeclined = true;

            const { completed } = await createContribution(data);

            if (completed) {
                navigate(this.urls.base);
            } else {
                this.setState({ shouldShowDonationTimeout: true });
            }
        } catch (err) {
            console.error(err.response ? err.response.data : err);
            setSubmitting(false);

            showFlash({
                type: 'danger',
                message: `There was a problem processing your ${contributionName}. Your credit card was NOT charged. Please retry your contribution.`,
                duration: 5000,
            });
        }
    };

    handleThirdParty = () => {
        const { thirdPartyTermsAccepted } = this.state;

        this.setState({ thirdPartyTermsAccepted: !thirdPartyTermsAccepted });
    };

    renderSelectType = () => {
        const { campaign } = this.props;

        if (campaign.isDonationOnly) {
            return <Redirect to={this.urls.donateAmount} />;
        }

        return <SelectType disablePledge={!this.canPledge} onSelect={this.handleTypeSubmit} />;
    };

    renderSelectAmount = () => {
        const { type } = this.state;

        if (!type || (type === 'pledge' && !this.canPledge)) {
            return <Redirect to={this.prevStepUrl} />;
        }

        return <SelectAmount onSelect={this.handleAmountSubmit} />;
    };

    renderPaymentEntry = () => {
        const { amount } = this.state;
        if (!amount) return <Redirect to={this.prevStepUrl} />;

        return <EnterPayment onSubmit={this.handlePaymentSubmit} />;
    };

    renderConfirmation = () => {
        const { token, user } = this.state;
        if (!token || !user) return <Redirect to={this.prevStepUrl} />;

        return (
            <Confirmation
                onConfirm={this.handleConfirm}
                onThirdPartyAccepted={this.handleThirdParty}
            />
        );
    };

    render() {
        const { step, campaign, showFlash, navigate, isReferrerLocked } = this.props;
        const {
            type,
            amount,
            backpledge,
            tip,
            token,
            card,
            user,
            fundraiser,
            shouldPromptBackpledge,
            shouldShowDonationTimeout,
            thirdPartyTermsAccepted,
        } = this.state;

        const isPledge = type === 'pledge';

        if (campaign.isComplete) {
            showFlash({
                type: 'info',
                title: 'This campaign has ended',
                message: 'Contributions to this campaign are no longer able to be made.',
                duration: 5000,
            });

            return <Redirect to={this.urls.base} />;
        }

        return (
            <ContributePageProvider
                campaign={campaign}
                contribution={{
                    type,
                    amount,
                    backpledge,
                    tip,
                    token,
                    card,
                    user,
                    fundraiser,
                    thirdPartyTermsAccepted,
                }}
                step={step}
                canPledge={this.canPledge}
                isReferrerLocked={isReferrerLocked}
            >
                <Container className="py-8 xxxl:py-24 flex-1 flex flex-col justify-start">
                    <SEO />
                    <Header
                        backUrl={this.prevStepUrl}
                        urls={this.urls}
                        activeStep={step}
                        type={type}
                        onToggleZendesk={this.handleZendesk}
                    />

                    <div className="pt-8 pb-12 md:py-16 xxxl:py-24 max-w-lg w-full mx-auto flex-1">
                        {{
                            type: () => this.renderSelectType(),
                            amount: () => this.renderSelectAmount(),
                            payment: () => this.renderPaymentEntry(),
                            confirm: () => this.renderConfirmation(),
                        }[step]()}
                    </div>
                </Container>

                {isPledge && (
                    <BackpledgePrompt
                        show={shouldPromptBackpledge}
                        onAccept={() => this.handleBackpledgeSubmit(true)}
                        onDecline={() => this.handleBackpledgeSubmit(false)}
                        onClose={() => this.handleBackpledgeSubmit(false)}
                    />
                )}

                {!isPledge && (
                    <DonationTimeout
                        show={shouldShowDonationTimeout}
                        onClose={() => navigate(this.urls.base)}
                    />
                )}
            </ContributePageProvider>
        );
    }
}

export default ContributePage;
